// The production datapoints-fetch seam injected into the timeseries
// dataset provider. The provider itself is engine-side and shape-only; the
// actual CDF round-trip — including cross-cell request coalescing — lives
// here, behind the narrow `DatapointsFetch` callback the provider closes
// over at registration.
//
// Batching is shared with the per-cell ts-ref fetcher: both route through
// the same per-`QueryClient` `BatchState` (microtask queue + pinned default
// window) so a top-level `(datapoints …)` row and a view's per-cell
// timeseries column coalesce into one CDF call when their fetch specs match.
// Reusing the one batch scope keeps the two source paths from drifting into
// separate, uncoalesced queues.
//
// The default fetch window is pinned ONCE per `QueryClient` (never the wall
// clock at fetch time) and handed to the provider as the registration-time
// `defaultWindow`, so a series source's cache key is stable across turns.

import type { DatapointsFetch } from '#core/lib/expr/providers/timeseries.ts';
import type { DatapointsBatchResult } from '#core/lib/timeseries/fetch-datapoints-batched.ts';

import { bucketToFetch, seriesToColumns } from './helpers.ts';
import { useBatchState } from '#plugin-chat/lib/source-fetchers/useBatchState.ts';

/**
 * Build the timeseries provider's `fetchDatapoints` seam plus the pinned
 * `defaultWindow` it registers with, closing over a `CogniteClient` and a
 * `QueryClient` (the batch scope). Closing over the `QueryClient` is what
 * lets a top-level `(datapoints …)` row and a view's per-cell timeseries
 * column share one request-coalescing batch when their fetch specs match.
 *
 * The returned `defaultWindow` is the `[startISO, endISO]` pair pinned once
 * for `queryClient`; the provider stores it on every series source's
 * `native.window`, so `fetchDatapoints` reads the window back off `native`
 * (the lazily-absorbed `time-bucket` hint may narrow it) rather than
 * re-reading the wall clock — the source cache key stays stable.
 *
 * `fetchDatapoints` translates the absorbed bucket hint into a concrete CDF
 * fetch spec via {@link bucketToFetch}, enqueues the request into the
 * shared per-`QueryClient` batch (so it coalesces with sibling cells), and
 * projects the response into the column-major inner shape via
 * {@link seriesToColumns}. A series missing from the response (unknown
 * externalId or no data in the window) resolves to `undefined`, which the
 * provider mints as an empty inner dataset.
 */
export function useDatapointsFetch(): DatapointsFetch {
  const { enqueue } = useBatchState();
  return async (native) => {
    // The series source's window (registration default, possibly narrowed by
    // an absorbed `time-bucket`) is the batch's fallback range; the bucket
    // hint's own `:window` overrides it inside `bucketToFetch`.
    const windowStart = Date.parse(native.window[0]);
    const windowEnd = Date.parse(native.window[1]);
    const { spec, specKey, aggField } = bucketToFetch(native.bucket, windowStart, windowEnd);
    const series = await new Promise<DatapointsBatchResult | undefined>((resolve, reject) => {
      enqueue({ space: native.ref.space, externalId: native.ref.externalId, specKey, spec, resolve, reject });
    });
    return series === undefined ? undefined : seriesToColumns(series, aggField);
  };
}
